// Módulo de oferta en VISTA PREVIA (blocks/skindion-offer-preview.liquid). Script clásico: la lógica pura
// vive en window.SkindionOffer para probarla en Node (tools/purchase-module.test.mjs).
// La compra desde la PDP es de 1 unidad (misión 07b): el botón y el sticky revisan el máximo de dispositivos (rosa y
// negro suman, contando lo que ya hay en el carrito) y, si se pasaría, muestran el aviso en la zona de error del botón
// usado. Después de esa revisión, con el carrito de la promo prendido (data-transactional="true", misión 07d-carrito)
// agregan el Skindion con su juego por assets/skindion-cart.js; si no, no agregan nada.
(() => {
  const logic = (window.SkindionOffer = window.SkindionOffer || {
    /** Dispositivos de la oferta que ya están en el carrito (/cart.js), rosa y negro sumados. */
    devicesInCart(cart, handles) {
      return (cart?.items ?? []).reduce((sum, item) => (handles.includes(item.handle) ? sum + item.quantity : sum), 0);
    },
    /** ¿Agregar `quantity` (1 desde la PDP) dejaría más dispositivos que el máximo? */
    addWouldExceed(inCart, limit, quantity = 1) {
      return inCart + quantity > limit;
    },
    /** El sticky aparece solo cuando el botón principal ya quedó ARRIBA del viewport y no se ve el pie. */
    stickyVisible({ ctaAbove, footerVisible }) {
      return Boolean(ctaAbove) && !footerVisible;
    },
  });

  if (customElements.get('skindion-offer')) return;

  class SkindionOfferElement extends HTMLElement {
    #inCart = 0;
    #limit = 2;
    #handles = [];
    #sticky = null;
    #ctaAbove = false;
    #footerVisible = false;
    #adding = false;
    #abort = new AbortController();

    connectedCallback() {
      const { signal } = this.#abort;
      this.#limit = Number(this.dataset.limit) || 2;
      this.#inCart = Number(this.dataset.inCart) || 0;
      this.#handles = (this.dataset.handles || '').split(',').filter(Boolean);

      // `position: fixed` falla dentro de contenedores con transform: el sticky vive en <body>.
      this.#sticky = this.querySelector('[data-offer-sticky]');
      if (this.#sticky) document.body.appendChild(this.#sticky);

      const cta = this.querySelector('[data-offer-cta]');
      cta?.addEventListener('click', () => this.#onCta(this), { signal });
      this.#sticky?.querySelector('[data-offer-cta-sticky]')?.addEventListener('click', () => this.#onCta(this.#sticky), { signal });

      // Se mide en cada scroll (en fase de captura): en escritorio Horizon desplaza dentro de .page-wrapper,
      // no en window, y un IntersectionObserver no avisa si el scroll salta sin cruzar el botón.
      const footer = document.querySelector('footer') ?? document.querySelector('[class*="footer-group"]');
      if (cta && this.#sticky) {
        const measure = () => {
          this.#ctaAbove = cta.getBoundingClientRect().bottom < 0;
          this.#footerVisible = footer ? footer.getBoundingClientRect().top < window.innerHeight : false;
          this.#updateSticky();
        };
        // Se lee justo después del pintado, con el layout ya calculado: leer dentro del fotograma lo forzaba (hasta 487 ms
        // con CPU 4x, L5). El sticky cambia a lo sumo un fotograma después. Sin medición al conectar: el sticky arranca
        // oculto, y si el navegador restaura el scroll, eso dispara un evento scroll.
        let frame = 0;
        const schedule = () => {
          if (frame) return;
          frame = requestAnimationFrame(() => {
            setTimeout(() => {
              frame = 0;
              measure();
            }, 0);
          });
        };
        document.addEventListener('scroll', schedule, { capture: true, passive: true, signal });
        window.addEventListener('resize', schedule, { passive: true, signal });
      }

      // El carrito puede cambiar sin recargar (drawer): se vuelve a leer para respetar el máximo.
      import('@shopify/events')
        .then(({ StandardEvents }) => {
          if (signal.aborted) return;
          document.addEventListener(StandardEvents.cartLinesUpdate, () => this.#refreshCart(), { signal });
        })
        .catch(() => {});
    }

    disconnectedCallback() {
      this.#abort.abort();
      this.#ctaAbove = false;
      this.#updateSticky();
      this.#sticky?.remove();
    }

    /** @param {HTMLElement} scope - El módulo o el sticky: su zona de error es la que habla. */
    async #onCta(scope) {
      const error = scope.querySelector('[data-offer-error]');
      if (logic.addWouldExceed(this.#inCart, this.#limit)) {
        if (error) error.textContent = this.dataset.limitMessage ?? '';
        return;
      }
      if (error) error.textContent = '';
      // Sin el carrito de la promo (data-transactional="false") no agrega nada, como en la 07b.
      if (this.dataset.transactional !== 'true' || this.#adding) return;

      // Misión 07d-carrito: el Skindion con su juego en una sola petición (assets/skindion-cart.js). El drawer se abre
      // cuando Shopify responde sin error (cart-drawer.js).
      const buttons = [this.querySelector('[data-offer-cta]'), this.#sticky?.querySelector('[data-offer-cta-sticky]')];
      this.#adding = true;
      for (const b of buttons) b?.setAttribute('aria-busy', 'true');
      try {
        const { addPromoDevice } = await import('@theme/skindion-cart');
        const cart = await addPromoDevice(Number(this.dataset.variantId), scope);
        this.#inCart = logic.devicesInCart(cart, this.#handles);
      } catch (e) {
        if (error) error.textContent = e?.response?.description || e?.response?.message || this.dataset.networkError || 'No pudimos agregar el producto. Revisa tu conexión e intenta de nuevo.';
      } finally {
        this.#adding = false;
        for (const b of buttons) b?.removeAttribute('aria-busy');
      }
    }

    async #refreshCart() {
      try {
        const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart.js`, { headers: { Accept: 'application/json' } });
        this.#inCart = logic.devicesInCart(await response.json(), this.#handles);
        // Si ya hay sitio, el aviso anterior deja de ser cierto.
        if (!logic.addWouldExceed(this.#inCart, this.#limit)) {
          for (const el of [this, this.#sticky]) el?.querySelectorAll('[data-offer-error]').forEach((e) => (e.textContent = ''));
        }
      } catch {
        // Sin red: se queda el último valor conocido; el backend sigue siendo quien limita.
      }
    }

    #updateSticky() {
      if (!this.#sticky) return;
      const visible = logic.stickyVisible({ ctaAbove: this.#ctaAbove, footerVisible: this.#footerVisible });
      this.#sticky.dataset.stuck = String(visible);
      // La burbuja de Chatwoot sube encima del sticky (snippets/skindion-chatwoot-widget.liquid).
      document.documentElement.classList.toggle('pdp-sticky-on', visible);
      if (visible) document.documentElement.style.setProperty('--pdp-sticky-h', `${this.#sticky.offsetHeight}px`);
    }
  }

  customElements.define('skindion-offer', SkindionOfferElement);
})();
